import { Controller } from '@hotwired/stimulus';

// Browser → USB thermal printer, ESC/POS, no print dialog.
//
// The server builds the receipt (Receipt, and the host's presenter behind
// its receipt route); this only carries bytes.
// WebUSB grants stick to the origin, so pairing is a one-time gesture: after
// it, getDevices() finds the printer again and every receipt is silent.
//
// Chrome and Edge only. Safari and Firefox — and any box where the OS holds
// the printer (Windows usbprint) — fall through to the print dialog with the
// text receipt. See fire/docs/POS.md.
//
// The gaveta hangs off the same printer (docs/POS.md § Drawer): `kick` is a
// Kick's open url (flash[:kick]); its bytes come once, then the sensor is
// asked until the drawer shuts, and the shut goes back to the log.
export default class extends Controller {
  static targets = ['pair', 'ready', 'none', 'width', 'raster', 'pin', 'polarity', 'open'];
  static values = {
    auto: String, // receipt urls, space apart, to print the moment we land
    kick: String, // a Kick's open url: the drawer, once
  };

  async connect() {
    await this.paint();
    // The door and the counter press nothing: a sale that just happened
    // prints itself, but only on a box that already has a printer.
    // One paper per url, in order: a printer's buffer takes one receipt at a time.
    if (!this.device) return;
    for (const url of this.autoValue.split(/\s+/).filter(Boolean)) await this.send(url);
    if (this.kickValue) await this.kick(this.kickValue);
  }

  disconnect() {
    this.gone = true; // a poll still running stops at its next turn
  }

  get usb() {
    return navigator.usb;
  }

  // What the paired printer is: 80mm/58mm paper, whether its firmware can
  // draw a QR, which DK pin kicks its drawer (2, or 5 on a Control iD) and
  // which sensor level means open. Lives in the browser because it describes
  // this counter; the field sets them once.
  get setup() {
    return {
      w: localStorage.getItem('printer:w') || '48',
      qr: localStorage.getItem('printer:qr') || 'native',
      pin: localStorage.getItem('printer:pin') || '2',
      open: localStorage.getItem('printer:open') || 'high',
    };
  }

  save() {
    if (this.hasWidthTarget) localStorage.setItem('printer:w', this.widthTarget.value);
    if (this.hasRasterTarget) localStorage.setItem('printer:qr', this.rasterTarget.checked ? 'raster' : 'native');
    if (this.hasPinTarget) localStorage.setItem('printer:pin', this.pinTarget.value);
    if (this.hasPolarityTarget) localStorage.setItem('printer:open', this.polarityTarget.value);
  }

  async pair() {
    try {
      this.device = await this.usb.requestDevice({
        filters: [{ classCode: 7 }, { classCode: 0xff }],
      });
      await this.paint();
    } catch {
      // The chooser was dismissed — nothing to say, nothing paired.
    }
  }

  async print({ params: { url } }) {
    await this.send(url);
  }

  async send(url) {
    const { w, qr } = this.setup;
    // A url may already carry its own question (?out=1 is the cash desk's).
    const src = `${url}${url.includes('?') ? '&' : '?'}w=${w}&qr=${qr}`;
    if (!this.device) return this.dialog(src);

    try {
      const res = await fetch(src);
      const bytes = new Uint8Array(await res.arrayBuffer());
      await this.write(bytes);
    } catch (e) {
      console.error('print failed', e);
      this.device = null;
      this.dialog(src); // paper, one way or another
    }
  }

  // The drawer's open, once: the server answers 410 with nothing on a
  // second ask, so a replay or a shared url never opens it again.
  async kick(url) {
    const res = await fetch(`${url}?pin=${this.setup.pin}`, {
      method: 'POST',
      headers: this.csrf,
    });
    if (!res.ok) return;
    await this.write(new Uint8Array(await res.arrayBuffer()));
    await this.watch(url.replace(/\/open$/, '/shut'));
  }

  // The sensor, every half second: open is shown and said (printer:drawer),
  // and the next closed reading goes back as the shut. Never open within
  // 5 s: no sensor wired, or the polarity is the other one. Nothing said.
  async watch(shut) {
    const nap = () => new Promise((ok) => setTimeout(ok, 500));
    let seen = false;
    for (let tries = 0; !this.gone && (seen || tries < 10); tries++) {
      await nap();
      const open = await this.drawer();
      if (open === null) return;
      if (open && !seen) {
        seen = true;
        this.opened(true);
      }
      if (!open && seen) {
        this.opened(false);
        await fetch(shut, { method: 'PATCH', headers: this.csrf });
        return;
      }
    }
  }

  // DLE EOT 1: one status byte back, bit 2 the level of DK pin 3. Null when
  // the printer does not answer.
  async drawer() {
    try {
      const [byte] = await this.write(new Uint8Array([0x10, 0x04, 0x01]), 1);
      if (byte === undefined) return null;
      return Boolean(byte & 0x04) === (this.setup.open === 'high');
    } catch {
      return null;
    }
  }

  opened(open) {
    if (this.hasOpenTarget) this.openTarget.classList.toggle('hidden', !open);
    this.dispatch('drawer', { detail: { open } });
  }

  get csrf() {
    return {
      'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content,
    };
  }

  // Claim, write in chunks a small printer buffer can swallow, read back
  // `read` bytes if asked (a status, 1 s at most), let go.
  async write(bytes, read = 0) {
    const d = this.device;
    await d.open();
    if (!d.configuration) await d.selectConfiguration(1);
    const iface = d.configuration.interfaces.find((i) => i.alternate.endpoints.some((e) => e.direction === 'out'));
    await d.claimInterface(iface.interfaceNumber);
    const out = iface.alternate.endpoints.find((e) => e.direction === 'out');
    const back = iface.alternate.endpoints.find((e) => e.direction === 'in');

    try {
      for (let i = 0; i < bytes.length; i += 4096) {
        await d.transferOut(out.endpointNumber, bytes.slice(i, i + 4096));
      }
      if (!read || !back) return [];
      const late = new Promise((ok) => setTimeout(() => ok(null), 1000));
      const res = await Promise.race([d.transferIn(back.endpointNumber, read), late]);
      return res?.data ? Array.from(new Uint8Array(res.data.buffer, res.data.byteOffset, res.data.byteLength)) : [];
    } finally {
      await d.releaseInterface(iface.interfaceNumber);
    }
  }

  // Last rung of the ladder: the text receipt in the OS print dialog.
  async dialog(url) {
    const res = await fetch(`${url}&format=txt`, {
      headers: { Accept: 'text/plain' },
    });
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    document.body.appendChild(frame);
    const text = (await res.text()).replace(/[<&]/g, (c) => (c === '<' ? '&lt;' : '&amp;'));
    frame.contentDocument.write(`<pre style="font:12px monospace">${text}</pre>`);
    frame.contentDocument.close();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }

  async paint() {
    if (!this.usb) return this.show('none');
    [this.device] = await this.usb.getDevices();
    this.show(this.device ? 'ready' : 'pair');
    if (this.hasWidthTarget) this.widthTarget.value = this.setup.w;
    if (this.hasRasterTarget) this.rasterTarget.checked = this.setup.qr === 'raster';
    if (this.hasPinTarget) this.pinTarget.value = this.setup.pin;
    if (this.hasPolarityTarget) this.polarityTarget.value = this.setup.open;
  }

  show(state) {
    if (this.hasPairTarget) this.pairTarget.classList.toggle('hidden', state !== 'pair');
    if (this.hasReadyTarget) this.readyTarget.classList.toggle('hidden', state !== 'ready');
    if (this.hasNoneTarget) this.noneTarget.classList.toggle('hidden', state !== 'none');
  }
}
